import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// SECURITY NOTE: localStorage is vulnerable to XSS attacks
// For production, consider using httpOnly cookies or implementing
// a more secure token management strategy with refresh tokens
// Current implementation is acceptable for internal/trusted environments only

// Auth service
export const authService = {
  async register(email, password, fullName, invitationCode) {
    const response = await axios.post(`${API_BASE}/auth/register`, {
      email,
      password,
      full_name: fullName,
      invitation_code: invitationCode,
    });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  async login(email, password) {
    const response = await axios.post(`${API_BASE}/auth/login`, {
      email,
      password,
    });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getToken() {
    return localStorage.getItem('token');
  },

  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated() {
    return !!this.getToken();
  },
};

// API client with auth
const apiClient = axios.create({
  baseURL: API_BASE,
});

apiClient.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Reputation service
export const reputationService = {
  async createProfile(data) {
    const response = await apiClient.post('/reputation/profiles', data);
    return response.data;
  },

  async getProfiles() {
    const response = await apiClient.get('/reputation/profiles');
    return response.data;
  },

  async getProfile(profileId) {
    const response = await apiClient.get(`/reputation/profiles/${profileId}`);
    return response.data;
  },

  async updateProfile(profileId, updates) {
    const response = await apiClient.patch(`/reputation/profiles/${profileId}`, updates);
    return response.data;
  },

  async uploadProof(profileId, formData) {
    const response = await apiClient.post(
      `/reputation/profiles/${profileId}/proofs/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  async addProofUrl(profileId, data) {
    const response = await apiClient.post(
      `/reputation/profiles/${profileId}/proofs/url`,
      data
    );
    return response.data;
  },

  async getProofs(profileId) {
    const response = await apiClient.get(`/reputation/profiles/${profileId}/proofs`);
    return response.data;
  },

  async generateStrategy(profileId) {
    const response = await apiClient.post(
      `/reputation/profiles/${profileId}/strategy/generate`
    );
    return response.data;
  },

  async validateStrategy(strategyId, tier) {
    const response = await apiClient.post(`/reputation/strategies/${strategyId}/validate`, {
      validated_tier: tier,
    });
    return response.data;
  },

  async getStrategy(strategyId) {
    const response = await apiClient.get(`/reputation/strategies/${strategyId}`);
    return response.data;
  },

  async generateCalendar(strategyId) {
    const response = await apiClient.post(
      `/reputation/strategies/${strategyId}/calendar/generate`
    );
    return response.data;
  },

  async getCalendar(strategyId) {
    const response = await apiClient.get(`/reputation/strategies/${strategyId}/calendar`);
    return response.data;
  },

  async generateContent(data) {
    const response = await apiClient.post('/reputation/content/generate', data);
    return response.data;
  },

  async getContent(contentId) {
    const response = await apiClient.get(`/reputation/content/${contentId}`);
    return response.data;
  },

  async updateContent(contentId, updates) {
    const response = await apiClient.patch(`/reputation/content/${contentId}`, updates);
    return response.data;
  },

  async approveContent(contentId) {
    const response = await apiClient.post(`/reputation/content/${contentId}/approve`);
    return response.data;
  },

  async scheduleContent(contentId, scheduledAt) {
    const response = await apiClient.post(`/reputation/content/${contentId}/schedule`, {
      scheduled_at: scheduledAt,
    });
    return response.data;
  },

  async publishToWordPress(contentId, outletId) {
    const response = await apiClient.post(
      `/reputation/content/${contentId}/publish-wordpress`,
      { outlet_id: outletId }
    );
    return response.data;
  },

  async getDashboard(profileId) {
    const response = await apiClient.get(`/reputation/dashboard/${profileId}`);
    return response.data;
  },
};

// Outlet service
export const outletService = {
  async getOutlets() {
    const response = await apiClient.get('/outlets');
    return response.data;
  },

  async createOutlet(data) {
    const response = await apiClient.post('/outlets', data);
    return response.data;
  },

  async updateOutlet(outletId, updates) {
    const response = await apiClient.patch(`/outlets/${outletId}`, updates);
    return response.data;
  },

  async testOutlet(outletId) {
    const response = await apiClient.post(`/outlets/${outletId}/test`);
    return response.data;
  },

  // Admin routes
  async createGlobalOutlet(data) {
    const response = await apiClient.post('/outlets/admin/global', data);
    return response.data;
  },

  async getAllOutletsAdmin() {
    const response = await apiClient.get('/outlets/admin/all');
    return response.data;
  },

  async updateGlobalOutlet(outletId, updates) {
    const response = await apiClient.patch(`/outlets/admin/${outletId}`, updates);
    return response.data;
  },

  async deleteOutlet(outletId) {
    const response = await apiClient.delete(`/outlets/admin/${outletId}`);
    return response.data;
  },
};

// Billing service
export const billingService = {
  async createCheckoutSession(tier) {
    const response = await apiClient.post('/billing/create-checkout-session', { tier });
    return response.data;
  },

  async createPortalSession() {
    const response = await apiClient.post('/billing/create-portal-session');
    return response.data;
  },

  async getSubscriptionStatus() {
    const response = await apiClient.get('/billing/subscription-status');
    return response.data;
  },
};

// Profitability service
export const profitabilityService = {
  async getMissionTracking(profileId) {
    const response = await apiClient.get(`/profitability/mission-tracking/${profileId}`);
    return response.data;
  },

  async downloadMissionReportPDF(profileId) {
    const response = await apiClient.get(`/profitability/mission-report/${profileId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getUpsellSuggestions(profileId) {
    const response = await apiClient.get(`/profitability/upsell-suggestions/${profileId}`);
    return response.data;
  },

  async calculatePlan(tier, budget) {
    const response = await apiClient.post('/profitability/calculate-plan', { tier, budget });
    return response.data;
  },
};

export default apiClient;
